﻿import { SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from "react-router-dom";
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faSquare, faSquareCheck } from "@fortawesome/free-regular-svg-icons";
import { DateTime } from "luxon";
import { Autocomplete, TextField } from "@mui/material";
import { useSelector } from "react-redux";

import { useQueryClient } from "@tanstack/react-query";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable } from "../../components/DataTable/TableHook";
import { useOnlineStatus } from "../../components/Hooks/useOnlineStatus";
import { StoppageCalc } from "../../DTOs/Stoppage/StoppageCalc";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { stoppagesMutations } from "../../offline/stoppagesMutations";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import NestedOperationsTable from "./NestedOperationsTable";
import { StoppageDto } from "../../DTOs/Stoppage/StoppageDto";
import { useSnackbar } from "notistack";
import { operationsAjax } from "../../ajax/OperationsAjax";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { useOptions } from "../../components/Hooks/useOptions";
import { Option } from "../../DTOs/Common/OptionResponse";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { TripleShift } from "../../types/Shift";
import { FilterConfig } from "../../components/DataTable/TableFiltersElement";
import { RootState } from "../../state/store";
import { Cancelled } from "../../types/ErrorTypes";

type StatusCellProps = {
  rowId: number;
  status: string;
  onClick: (id: number) => Promise<void>;
};

interface OperationsTableProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  stoppageTypeGroupOptions: {
    value: number,
    label: string,
    groupColor: string,
    stoppageType: string,
    downtimeTypeId: number
  }[];
  locationOptions: LocationDTO[];
  isLocationOptionsLoading: boolean;
  setRefreshFn?: (fn: () => void) => void;
}

const OperationsTable: React.FC<OperationsTableProps> = ({ selectedDate, selectedShift, stoppageTypeGroupOptions, locationOptions, isLocationOptionsLoading, setRefreshFn }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const { connection } = useSignalRContext();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const refreshNestedTableRef = useRef<(() => void) | null>(null);
  const refreshNestedTable = useCallback(() => {
    if (refreshNestedTableRef.current) refreshNestedTableRef.current();
    else console.warn("Tried to refresh nested row before it was ready");
  }, []);
  const [tableFilters, setTableFilters] = useState<FilterConfig[]>([
    {
      key: 'Status',
      label: 'Statuses',
      type: 'dropdown',
      addAllValuesSelector: true,
      valueIndex: 0,
      possibleValues: ['UP', ...Array.from(new Set(stoppageTypeGroupOptions.map(o => o.stoppageType)))],
      placeholder: 'Filter by Status',
      value: ''
    },
    {
      key: 'MachineName',
      label: 'Machines',
      type: 'text',
      valueIndex: 1,
      placeholder: 'Filter by Machine Name',
      value: ''
    },
  ]);
  const { options: jobTypes } = useOptions<Option<number>, Option<number>>(
    ['references', 'jobTypes'],
    () => referencesAjax.getJobTypes(),
    o => o,
  );

  const updateEngineHours = useCallback(async (operationId: number, startHours: number, endHours: number) => {
    const response = await operationsAjax.updateEngineHours(operationId, startHours, endHours)

    if (response instanceof ErrorResponse) {
      enqueueSnackbar("Error while updating the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })
      return false;
    }

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    updateRowSource((row) => row.plantId === response.data?.plantId, response.data!);

    enqueueSnackbar("Hours updated successfully", { variant: 'success', autoHideDuration: 5000 })

    return true;
  }, [enqueueSnackbar])

  const updateLocation = useCallback(async (operationId: number, locationId: number) => {
    const response = await operationsAjax.updateOperationLocation(operationId, locationId);

    if (response instanceof ErrorResponse) {
      enqueueSnackbar("Error while updating the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })
      return false;
    }

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    updateRowSource((row) => row.plantId === response.data?.plantId, response.data!);
    enqueueSnackbar("Location updated successfully", { variant: 'success', autoHideDuration: 5000 })

    return true;
  }, [enqueueSnackbar])

  const refreshPlant = useCallback(async (plantId: number) => {
    if (!isOnline) return true;

    const response = await operationsAjax.getStoppageCalcsDataForPlant(selectedDate, selectedShift, plantId)
    if (response instanceof ErrorResponse) {
      enqueueSnackbar("Error while refreshing the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })
      return false;
    }

    updateRowSource((row) => row.plantId === response.plantId, response);
    return true;
  }, [selectedDate, selectedShift, enqueueSnackbar, isOnline]);

  const setAsAvailable = useCallback(async (stoppage: StoppageDto) => {
    let isValid = true;

    if (!stoppage.plantId || !stoppage.stoppageSubTypeId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (!isValid)
      return false;

    try {
      const response = await stoppagesMutations.updateStoppage(stoppage);

      if (!response.success && !response.data) {
        (response as any).errors?.forEach((error: any, index: number) => {
          enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
        })

        return false;
      }

      enqueueSnackbar('Status updated', { variant: 'success', autoHideDuration: 3000 })

      if (!isOnline) {
        mutateCachedResult(old => ({
          ...old,
          data: old.data.map(r => r.plantId === stoppage.plantId
            ? { ...r, status: 'UP', stoppageId: undefined, stoppageColourHub: '', statusDescription: '', currentStoppageTypeId: undefined, currentStoppageSubTypeId: undefined }
            : r
          )
        }));
      } else {
        const updatedOperationResponse = await operationsAjax.getStoppageCalcsDataForPlant(selectedDate, selectedShift, stoppage.plantId)

        if (updatedOperationResponse instanceof ErrorResponse) {
          enqueueSnackbar("Error while refreshing the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })

          return false;
        }

        updateRowSource((row) => row.plantId === updatedOperationResponse.plantId, updatedOperationResponse);
      }
    }
    catch (error) {
      if (error === Cancelled) return false;
      enqueueSnackbar("Error while refreshing the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })

      return false;
    }

    refreshNestedTable();
    return true;
  }, [enqueueSnackbar, selectedDate, selectedShift, refreshNestedTable, isOnline]);

  const getRows = useCallback(async () => {
    const filters = {
      ...tableFilters.reduce((acc, cur) => {
        acc[cur.key] = cur.value ?? '';
        return acc;
      }, {} as Record<string, string>), pageNumber: 0, pageSize: 10
    };

    return operationsAjax.getStoppageCalcsData(selectedDate, selectedShift, filters);
  }, [selectedDate, selectedShift, tableFilters]);

  const translateRows = useCallback((r: StoppageCalc): TableRow => {
    return {
      id: r.plantId,
      values: [
        {
          name: 'Status',
          type: 'custom',
          value: r.status,
          tailwindClasses: `basis-[8%] justify-center`,
          render: (value) => {
            return (
              <span
                style={{
                  backgroundColor: r.status !== "UP" ? r.stoppageColourHub : ""
                }}
                className={`px-1 py-3 text-white font-bold h-full inline-flex w-full items-center justify-center ${r.status === "UP" ? 'bg-theme-success' : ""}`}>{value.value}</span>
            );
          },
        },
        {
          name: 'Toggle Available', type: 'custom', value: 'custom', tailwindClasses: 'basis-[6%]',
          render: (_, row) => r.status !== 'UP' && <StatusCell
            rowId={row.id}
            status={r.status}
            onClick={async () => {
              const merged = selectedDate
                .set({
                  hour: DateTime.now().hour,
                  minute: DateTime.now().minute,
                  second: DateTime.now().second,
                  millisecond: DateTime.now().millisecond
                });
              await setAsAvailable(new StoppageDto({
                stoppageId: r.stoppageId,
                plantId: r.plantId,
                startTime: r.timeIn || DateTime.now(),
                endTime: merged,
                incident: false,
                stoppageTypeId: r.currentStoppageTypeId ?? 0,
                stoppageSubTypeId: r.currentStoppageSubTypeId ?? 0,
                locationId: r.locationId
              }));
            }}
          />
        },
        {
          name: 'Machine', sortable: true, type: 'string', value: r.machineName, tailwindClasses: 'basis-[8%] py-3',
          render: (val) => {
            switch (r.latestRowJobType) {
              case jobTypes.find(j => j.label === 'Production Digging')?.key:
                return (
                  <Link
                    to={`/production/show?diggingPatternId=${r.latestRowId}`}
                    target="_blank"
                    className="px-4 py-1 rounded bg-theme-info hover:bg-theme-info-hover transition text-white rounded-sm border-none"
                  >
                    <span>{val.value}</span>
                  </Link>
                )
              case jobTypes.find(j => j.label === 'Blast Hole Drilling')?.key:
                return (
                  <Link
                    to={`/db/bh-drilling?drillingId=${r.latestRowId}`}
                    target="_blank"
                    className="px-4 py-1 rounded bg-theme-info hover:bg-theme-info-hover transition text-white rounded-sm border-none"
                  >
                    <span>{val.value}</span>
                  </Link>
                )
              case jobTypes.find(j => j.label === 'Loader')?.key:
                return (
                  <Link
                    to={`/production/crushing`}
                    target="_blank"
                    className="px-4 py-1 rounded bg-theme-info hover:bg-theme-info-hover transition text-white rounded-sm border-none"
                  >
                    <span>{val.value}</span>
                  </Link>
                )
              case jobTypes.find(j => j.label === 'Digging')?.key:
                return (
                  <Link
                    to={`/production/rehandle`}
                    target="_blank"
                    className="px-4 py-1 rounded bg-theme-info hover:bg-theme-info-hover transition text-white rounded-sm border-none"
                  >
                    <span>{val.value}</span>
                  </Link>
                )
              default:
                return (
                  <span>{val.value}</span>
                )
            }
          }
        },
        {
          name: 'Start Engine Hours', editable: true, type: 'decimal', value: r.startEngineHours, tailwindClasses: 'basis-[9%] py-3', disabled: isShiftLocked,
          onBlur: async (row, newValue) => {
            await updateEngineHours(r.operationId, newValue ?? 0, row.values.find(v => v.name === "End Engine Hours")?.value);
          }
        },
        {
          name: 'End Engine Hours', editable: true, type: 'decimal', value: r.endEngineHours, tailwindClasses: 'basis-[9%] py-3', disabled: isShiftLocked,
          onBlur: async (row, newValue) => {
            await updateEngineHours(r.operationId, row.values.find(v => v.name === "Start Engine Hours")?.value, newValue ?? 0);
          }
        },
        { name: 'Status Description', type: 'string', value: r.statusDescription, tailwindClasses: 'basis-[12%] py-3' },
        { name: 'Time In', type: 'string', value: r.timeIn ? (DateTime.isDateTime(r.timeIn) ? r.timeIn : DateTime.fromISO(r.timeIn as unknown as string, { setZone: true })).toLocal().toFormat('HH:mm') : undefined, tailwindClasses: 'basis-[7%] py-3' },
        {
          name: "Avail'",
          type: 'percentage',
          value: r.availabilityPercentage?.toFixed(2),
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "Total Hours",
          type: 'integer',
          value: r.totalHours?.toFixed(2),
          tailwindClasses: 'basis-[4%] py-3'
        },
        {
          name: "Standby",
          type: 'integer',
          value: r.calculatedNod ?? "N/A",
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "NOD Difference",
          type: 'custom',
          value: r.calculatedNod ?? "N/A",
          tailwindClasses: 'basis-[6%] py-3',
          render: () => {
            const diff = r.nodDifference ?? 0;
            const isDanger = diff > 0.2;

            return (
              <span className={`px-2 py-1 rounded ${isDanger ? "bg-theme-danger text-white" : ""}`}>
                {diff?.toFixed(2)}h
              </span>
            );
          }
        },
        {
          name: "UofA",
          type: 'percentage',
          value: r.utilizationOfAvailability?.toFixed(2),
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "Location", type: 'custom', value: r.locationId, tailwindClasses: 'basis-[16%] py-3', render: (tableValue, row) =>
            <Autocomplete
              value={locationOptions.find(o => o.id === tableValue.value) ?? null}
              classes={{ root: "w-full transition" }}
              loading={isLocationOptionsLoading}
              disabled={isLocationOptionsLoading || isShiftLocked}
              options={locationOptions}
              getOptionLabel={(o) => o.name}
              onChange={(_, opt) => { updateLocation(r.operationId, opt?.id ?? 0) }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Select Location"
                  size="small"
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {isLocationOptionsLoading ? <span className="animate-fade-in"><FontAwesomeIcon icon={faSpinner} spin /></span> : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      )
                    }
                  }}
                />
              )}
            />
        },
      ]
    }
  }, [isShiftLocked, selectedDate, setAsAvailable, jobTypes, updateEngineHours, locationOptions, isLocationOptionsLoading, updateLocation]);

  const prefetchStoppages = useCallback((entries: StoppageCalc[]) => {
    if (!isOnline) return;
    entries.forEach(entry => {
      queryClient.prefetchQuery({
        queryKey: ['dataTable', 'operations', 'plantStoppages', selectedDate.toISODate(), selectedShift, entry.plantId, entry.status, 1, 5, null, 'asc', false, null],
        queryFn: async () => {
          const result = await stoppagesAjax.getStoppagesByShiftAndPlant(selectedDate, selectedShift, entry.plantId);
          if (ErrorResponse.isErrorResponse(result)) throw new Error('Failed to load stoppages');
          return result;
        },
      });
    });
  }, [queryClient, isOnline, selectedDate, selectedShift]);

  const {
    displayData,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
    refresh,
    updateRowSource,
    mutateCachedResult,
  } = useDataTable<StoppageCalc>({
    queryKey: ['operations', 'stoppageCalcs', selectedDate.toISODate(), selectedShift, tableFilters.map(f => ({ key: f.key, value: f.value }))],
    fetchFn: getRows,
    translateFn: translateRows,
    initialPage: 1,
    pageSize: 5,
    onSuccessfulFetch: prefetchStoppages,
  });

  const StatusCell: React.FC<StatusCellProps> = ({ rowId, status, onClick }) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async () => {
      setIsLoading(true);
      await onClick(rowId);
      setIsLoading(false);
    };

    return (
      <span
        className="relative inline-flex w-full h-full items-center justify-center group cursor-pointer"
        onClick={handleClick}
      >
        {isLoading ? (
          <FontAwesomeIcon icon={faSpinner} spin className="text-theme-text transition-opacity duration-[.2s]" />
        ) : status !== "UP" && (
          <>
            <FontAwesomeIcon
              icon={faSquare}
              size="2x"
              className="transition-opacity duration-[.2s] absolute opacity-100 group-hover:opacity-0"
            />
            <FontAwesomeIcon
              icon={faSquareCheck}
              size="2x"
              className="transition-opacity duration-[.2s] absolute opacity-0 group-hover:opacity-100 text-theme-success"
            />
          </>
        )}
      </span>
    );
  };

  useEffect(() => {
    setTableFilters([
      {
        key: 'Status',
        label: 'Statuses',
        type: 'dropdown',
        addAllValuesSelector: true,
        valueIndex: 0,
        possibleValues: ['UP', ...Array.from(new Set(stoppageTypeGroupOptions.map(o => o.stoppageType)))],
        placeholder: 'Filter by Status',
        value: ''
      },
      {
        key: 'MachineName',
        label: 'Machines',
        type: 'text',
        valueIndex: 2,
        placeholder: 'Filter by Machine Name',
        value: ''
      },
    ])

  }, [stoppageTypeGroupOptions]);

  const updateTableFilters = useCallback((filters: SetStateAction<FilterConfig[]>) => {
    setTableFilters(filters);
  }, []);

  useEffect(() => {
    if (setRefreshFn) setRefreshFn(() => refresh);
  }, [refresh, setRefreshFn]);

  useEffect(() => {
    if (!connection) return;

    const handleDataUpdated = async (jsonData: string) => {
      try {
        const data = JSON.parse(jsonData);
        const plantIdToUpdate = data.plantId;

        if (plantIdToUpdate) {
          const updatedRowDto = await operationsAjax.getStoppageCalcsDataForPlant(
            selectedDate, selectedShift, plantIdToUpdate
          );

          if (updatedRowDto instanceof ErrorResponse) {
            enqueueSnackbar("Live updates for this page have been interrupted. Please refresh the page", { variant: 'error', autoHideDuration: 5000 });
            return;
          }

          updateRowSource(
            oldRow => oldRow.plantId === updatedRowDto.plantId,
            updatedRowDto
          );
          refreshNestedTable();
          enqueueSnackbar(`${updatedRowDto.machineName} has been updated`, { variant: 'info', autoHideDuration: 5000 })
        }
      } catch (error) {
        if (error === Cancelled) return;
        console.error("Failed to parse DataUpdatedAsync JSON", error);
      }
    };

    connection.on("DataUpdatedAsync", handleDataUpdated);

    return () => {
      connection.off("DataUpdatedAsync", handleDataUpdated);
    };
  }, [connection, refreshNestedTable, selectedDate, selectedShift, enqueueSnackbar, updateRowSource]);

  return (
    <DataTable
      data={displayData}
      isLoading={isLoading}
      isBackgroundFetching={isBackgroundFetching}
      error={error}
      pageNumber={pageNumber}
      setPage={setPage}
      sortField={sortField}
      sortDirection={sortDirection}
      tableFilters={tableFilters}
      setTableFilters={updateTableFilters}
      minTableWidthPixels={1320}
      maxTableWidthPixels={1680}
      disableContainer={true}
      virtualizeOver={20}
      fillHeight
      expandedRowRenderer={row => {
        return (
          <NestedOperationsTable
            plantId={row.id}
            selectedDate={selectedDate}
            selectedShift={selectedShift}
            stoppageTypeGroupOptions={stoppageTypeGroupOptions}
            parentRowStatus={row.values.find(v => v.name === 'Status')?.value}
            refreshParent={refreshPlant}
            registerRefreshFn={(fn: () => void) => {
              refreshNestedTableRef.current = fn;
            }}
          />
        )
      }}
    />
  )
};

export default OperationsTable;
